import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useAccount } from 'wagmi';
import toast from 'react-hot-toast';
import NavHeader from '../components/NavHeader';
import FooterSection from '../components/FooterSection';
import { GlassCard, CountUpNumber, ConnectWalletPrompt } from '../components/shared/GlassCard';
import { Pagination } from '../components/shared/Pagination';
import { StatusBadge } from '../components/shared/StatusBadge';
import { GradientText } from '../components/shared/GradientText';
import {
  computeHistoryStats,
  filterHistoryBets,
  getAutoDrawSettings,
  getCachedPlayerHistoryAllBets,
  getPlayerHistoryBundle,
  paginateHistoryBets,
} from '../api/playerApi';
import { formatGameRoundLabel } from '../api/analyticsApi.js';
import {
  formatAppliedDateRangeLabel,
  formatLocalISODate,
  getDefaultDateRange,
  mergeFiltersWithDateRange,
  parseLocalISODate,
} from '../utils/constants.ts';
import { GAMES } from '../data/games';
import AutoDrawModal from '../components/landing/AutoDrawModal';
import { formatPickedNumbersList } from '../api/gameApi';

const HISTORY_BETS_PAGE_SIZE = 5;
const HISTORY_FETCH_ALL_LIMIT = 100000;
const DEFAULT_HISTORY_DATE_RANGE = getDefaultDateRange();
const DATE_PICKER_INPUT_CLASS =
  'w-full min-w-[10rem] rounded-lg border border-white/10 bg-dark/60 px-3 py-2 text-sm text-white outline-none transition-colors focus:border-hash-gold/40';

function buildHistoryFilters(gameFilters, appliedDateRange) {
  return mergeFiltersWithDateRange(gameFilters, appliedDateRange);
}

function HistoryInlineLoader() {
  return (
    <div className="mb-3 shrink-0" aria-busy="true" role="status" aria-live="polite">
      <div
        className="h-1 w-full overflow-hidden rounded-full bg-hash-gold/15"
        role="progressbar"
        aria-valuetext="Loading history"
      >
        <div className="hm-analytics-bar-fill" />
      </div>
      <p className="mt-2 text-center text-xs font-medium text-hash-gold/90">
        Data is loading ...
      </p>
    </div>
  );
}

function betGameLabel(bet) {
  const name = GAMES[bet.gameId]?.shortName || bet.gameId;
  const roundId = bet.roundId ?? bet.drawId;
  return formatGameRoundLabel(name, roundId);
}

function formatBetPickDisplay(bet) {
  if (!bet?.gameId) return '—';
  const list =
    Array.isArray(bet.pickedNumberStrs) && bet.pickedNumberStrs.length > 0
      ? bet.pickedNumberStrs.map(String)
      : bet.pickedNumberStr != null && String(bet.pickedNumberStr) !== ''
        ? [String(bet.pickedNumberStr)]
        : bet.numbers?.length
          ? bet.numbers.map(String)
          : [];
  return formatPickedNumbersList(bet.gameId, list);
}

function TxLink({ txHash }) {
  if (!txHash || typeof txHash !== 'string' || !txHash.startsWith('0x')) {
    return <span className="text-gray-500">—</span>;
  }
  const href = `https://bscscan.com/tx/${txHash}`;
  const label = `${txHash.slice(0, 8)}…${txHash.slice(-6)}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-sm text-medora-blue hover:text-hash-gold font-mono underline-offset-2 hover:underline"
    >
      {label}
    </a>
  );
}

function resultLabel(result) {
  if (result === 'won_direct') return 'Won (Direct)';
  if (result === 'won_neighbor') return 'Won (Neighbor)';
  if (result === 'lost') return 'Lost';
  if (result === 'purchased') return 'Purchased';
  return result || 'Pending';
}

function formatPrizeDisplay(bet) {
  const p = bet?.prize;
  const n = typeof p === 'number' ? p : Number(p);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return `${n.toFixed(2)} USDT`;
}

function History() {
  let { address } = useAccount();
  const [allBets, setAllBets] = useState([]);
  const [filters, setFilters] = useState({ gameId: '', result: '' });
  const [dateFrom, setDateFrom] = useState(DEFAULT_HISTORY_DATE_RANGE.dateFrom);
  const [dateTo, setDateTo] = useState(DEFAULT_HISTORY_DATE_RANGE.dateTo);
  const [appliedDateRange, setAppliedDateRange] = useState(DEFAULT_HISTORY_DATE_RANGE);
  const [page, setPage] = useState(1);
  const [, setAutoDraw] = useState(null);
  const [autoDrawModalOpen, setAutoDrawModalOpen] = useState(false);
  const [historyRefreshing, setHistoryRefreshing] = useState(false);
  const maxSelectableDate = useMemo(() => new Date(), []);
  const fromSelectedDate = useMemo(() => parseLocalISODate(dateFrom), [dateFrom]);
  const toSelectedDate = useMemo(() => parseLocalISODate(dateTo), [dateTo]);
  const appliedRangeLabel = useMemo(
    () => formatAppliedDateRangeLabel(appliedDateRange),
    [appliedDateRange],
  );
  /** Date range only — backend fetch runs when this changes (Apply/Reset), not on pagination. */
  const dateRangeFetchFilters = useMemo(
    () => buildHistoryFilters({ gameId: '', result: '' }, appliedDateRange),
    [appliedDateRange],
  );
  const viewFilters = useMemo(
    () => buildHistoryFilters(filters, appliedDateRange),
    [filters, appliedDateRange],
  );

  const filteredBets = useMemo(
    () => filterHistoryBets(allBets, viewFilters),
    [allBets, viewFilters],
  );

  const stats = useMemo(() => computeHistoryStats(filteredBets), [filteredBets]);

  const history = useMemo(
    () => paginateHistoryBets(filteredBets, page, HISTORY_BETS_PAGE_SIZE),
    [filteredBets, page],
  );

  useEffect(() => {
    setPage(1);
  }, [filters.gameId, appliedDateRange, address]);

  useEffect(() => {
    if (!address) {
      setHistoryRefreshing(false);
      setAllBets([]);
      return;
    }
    let cancelled = false;

    const cached = getCachedPlayerHistoryAllBets(address, dateRangeFetchFilters);
    if (cached) {
      setAllBets(cached.allBets);
      setHistoryRefreshing(false);
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      setHistoryRefreshing(true);
      try {
        const [bundle, ad] = await Promise.all([
          getPlayerHistoryBundle(address, 1, HISTORY_FETCH_ALL_LIMIT, dateRangeFetchFilters),
          getAutoDrawSettings(address),
        ]);
        if (cancelled) return;
        setAllBets(bundle.history.bets);
        setAutoDraw(ad);
      } catch (e) {
        console.error('History load failed:', e);
        if (!cancelled) {
          setAllBets([]);
          setAutoDraw(null);
        }
      } finally {
        if (!cancelled) setHistoryRefreshing(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [address, dateRangeFetchFilters, autoDrawModalOpen]);

  const handlePageChange = useCallback((nextPage) => {
    setPage(nextPage);
  }, []);

  const handleGameFilterChange = useCallback((gameId) => {
    setFilters((prev) => ({ ...prev, gameId }));
    setPage(1);
  }, []);

  const handleApplyDateRange = useCallback(() => {
    if (dateFrom && dateTo && dateFrom > dateTo) {
      toast.error('Start date must be on or before end date');
      return;
    }
    const defaults = getDefaultDateRange();
    setHistoryRefreshing(true);
    setAppliedDateRange({
      dateFrom: dateFrom || defaults.dateFrom,
      dateTo: dateTo || defaults.dateTo,
    });
    setPage(1);
  }, [dateFrom, dateTo]);

  const handleClearDateRange = useCallback(() => {
    const defaults = getDefaultDateRange();
    setDateFrom(defaults.dateFrom);
    setDateTo(defaults.dateTo);
    setHistoryRefreshing(true);
    setAppliedDateRange(defaults);
    setPage(1);
  }, []);

  const showInitialLoader = historyRefreshing && allBets.length === 0;
  const showTopRefreshBar = showInitialLoader;
  const showDateRangeLoading = historyRefreshing && !showInitialLoader;

  if (!address) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-dark via-dark-secondary to-dark">
        <NavHeader />
        <main className="pt-nav pb-16">
          <ConnectWalletPrompt message="Connect your wallet to view your bet history and stats" />
        </main>
        <FooterSection />
      </div>
    );
  }

  const statCards = [
    { label: 'Total Bets', value: stats.totalBets },
    { label: 'Total Spent', value: stats.totalSpent, suffix: ' USDT' },
    { label: 'Total Won', value: stats.totalWon, suffix: ' USDT' },
    { label: 'Win Rate', value: stats.winRate, suffix: '%', decimals: 1 },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-dark via-dark-secondary to-dark relative">
      {showTopRefreshBar && (
        <>
          <style>
            {`
              @keyframes hmHistoryBarSlide {
                0% { transform: translateX(-100%); }
                100% { transform: translateX(400%); }
              }
              .hm-history-bar-fill {
                width: 40%;
                height: 100%;
                background: linear-gradient(90deg, transparent, rgba(238, 212, 91, 0.95), transparent);
                animation: hmHistoryBarSlide 1.1s ease-in-out infinite;
              }
            `}
          </style>
          <div
            className="fixed left-0 right-0 top-20 z-[60] flex flex-col border-b border-hash-gold/10 shadow-[0_4px_24px_rgba(0,0,0,0.35)]"
            aria-busy="true"
          >
            <div
              className="h-1 w-full overflow-hidden bg-hash-gold/15 shadow-[0_1px_0_rgba(0,0,0,0.4)]"
              role="progressbar"
              aria-valuetext="Loading history"
            >
              <div className="hm-history-bar-fill" />
            </div>
            {showInitialLoader && (
              <div
                className="flex items-center justify-center gap-2 border-b border-hash-gold/15 bg-dark/95 py-2 px-3 backdrop-blur-md sm:py-2.5"
                role="status"
                aria-live="polite"
              >
                <span
                  className="h-1.5 w-1.5 shrink-0 rounded-full bg-hash-gold shadow-[0_0_8px_rgba(238,212,91,0.6)] motion-safe:animate-pulse"
                  aria-hidden
                />
                <span className="text-center text-[11px] font-medium tracking-wide text-hash-gold/95 sm:text-xs">
                  Loading your bet history…
                </span>
              </div>
            )}
          </div>
        </>
      )}
      <div
        className="fixed inset-0 bg-cover bg-center bg-no-repeat pointer-events-none"
        style={{
          backgroundImage: 'url(/images/background-history.jpg)',
          opacity: 0.15,
        }}
      />
      <NavHeader />

      <main className={`pb-16 px-4 sm:px-6 lg:px-8 ${showInitialLoader ? 'pt-nav-loading' : 'pt-nav'}`}>
        <div className="container-premium max-w-5xl mx-auto">
          <h1 className="page-title text-3xl sm:text-4xl font-bold text-center">
            <GradientText variant="hash" className="text-3xl sm:text-4xl font-bold">Your</GradientText>{' '}
            <GradientText variant="medora" className="text-3xl sm:text-4xl font-bold">History</GradientText>
          </h1>
          <p className="page-subtitle text-gray-400 text-center">Track your bets, wins, and performance</p>
          {/* Filters */}
          <div className="mb-6 space-y-4">
            <div className="rounded-lg border border-white/10 bg-dark/40 p-4">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Date range</p>
                  <p className="mt-1 text-xs text-gray-500">
                    Filters bet history and stats. Default: last 24 hours through now.
                  </p>
                  {appliedRangeLabel && (
                    <p className="mt-2 text-xs font-medium text-hash-gold/90">
                      Active: {appliedRangeLabel}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end hm-analytics-datepicker">
                  <label className="flex min-w-[10rem] flex-col gap-1 text-xs text-gray-400">
                    From
                    <DatePicker
                      selected={fromSelectedDate}
                      onChange={(date) => setDateFrom(date ? formatLocalISODate(date) : '')}
                      maxDate={toSelectedDate || maxSelectableDate}
                      dateFormat="yyyy-MM-dd"
                      placeholderText="Start date"
                      className={DATE_PICKER_INPUT_CLASS}
                      calendarClassName="hm-analytics-datepicker-calendar"
                      popperClassName="hm-analytics-datepicker-popper"
                      showPopperArrow={false}
                      isClearable
                    />
                  </label>
                  <label className="flex min-w-[10rem] flex-col gap-1 text-xs text-gray-400">
                    To
                    <DatePicker
                      selected={toSelectedDate}
                      onChange={(date) => setDateTo(date ? formatLocalISODate(date) : '')}
                      minDate={fromSelectedDate}
                      maxDate={maxSelectableDate}
                      dateFormat="yyyy-MM-dd"
                      placeholderText="End date"
                      className={DATE_PICKER_INPUT_CLASS}
                      calendarClassName="hm-analytics-datepicker-calendar"
                      popperClassName="hm-analytics-datepicker-popper"
                      showPopperArrow={false}
                      isClearable
                    />
                  </label>
                  <button
                    type="button"
                    onClick={handleApplyDateRange}
                    disabled={historyRefreshing}
                    className="rounded-lg border border-hash-gold/30 bg-gradient-to-r from-hash-gold/20 to-medora-blue/20 px-4 py-2 text-sm font-semibold text-hash-gold transition-colors hover:bg-hash-gold/10 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Apply
                  </button>
                  <button
                    type="button"
                    onClick={handleClearDateRange}
                    disabled={historyRefreshing}
                    className="rounded-lg border border-white/10 px-4 py-2 text-sm font-medium text-gray-300 transition-colors hover:border-white/20 hover:text-white cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <div className="relative">
                <select
                  value={filters.gameId}
                  onChange={(e) => handleGameFilterChange(e.target.value)}
                  className="dark-input text-sm appearance-none pr-10"
                >
                  <option value="">All Games</option>
                  {Object.values(GAMES).map((g) => (
                    <option key={g.id} value={g.id}>{g.shortName}</option>
                  ))}
                </select>
                <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
          
          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {statCards.map((s, i) => (
              <GlassCard key={i} variant="gold" className="p-5 text-center">
                <div className="text-xl sm:text-2xl font-bold text-hash-gold text-shadow-gold mb-1">
                  <CountUpNumber end={s.value} suffix={s.suffix} decimals={s.decimals || 0} />
                </div>
                <div className="text-gray-500 text-xs">{s.label}</div>
              </GlassCard>
            ))}
          </div>



          {/* Bet History Table - Desktop */}
          <div className="hidden md:block">
            <GlassCard variant="premium" className="p-6 overflow-x-auto">
              {showDateRangeLoading && <HistoryInlineLoader />}
              <div className={showDateRangeLoading ? 'opacity-50 pointer-events-none' : ''}>
                <table className="dark-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Game</th>
                      <th>Number</th>
                      <th>Cost</th>
                      <th>Result</th>
                      <th>Prize</th>
                      <th>Tx</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.bets.map((bet) => (
                      <tr key={bet.id}>
                        <td className="text-gray-400 text-sm">{new Date(bet.timestamp).toLocaleDateString()}</td>
                        <td className="text-white font-medium">{betGameLabel(bet)}</td>
                        <td className="font-mono text-sm font-semibold tabular-nums text-hash-gold/95">
                          {formatBetPickDisplay(bet)}
                        </td>
                        <td className="text-white">{bet.cost.toFixed(2)} USDT</td>
                        <td>
                          <StatusBadge status={bet.result || bet.status}>
                            {resultLabel(bet.result)}
                          </StatusBadge>
                        </td>
                        <td className="text-hash-gold font-medium">{formatPrizeDisplay(bet)}</td>
                        <td>
                          <TxLink txHash={bet.txHash} />
                        </td>
                      </tr>
                    ))}
                    {history.bets.length === 0 && (
                      <tr>
                        <td colSpan={7} className="text-center text-gray-500 py-8">
                          {historyRefreshing ? 'Data is loading...' : 'No bets found'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </GlassCard>
          </div>

          {/* Bet History Cards - Mobile */}
          <div className="md:hidden space-y-3">
            {showDateRangeLoading && <HistoryInlineLoader />}
            <div className={showDateRangeLoading ? 'space-y-3 opacity-50 pointer-events-none' : 'space-y-3'}>
              {history.bets.map((bet) => (
                <GlassCard key={bet.id} variant="gold" className="p-4">
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <span className="text-white font-semibold">{betGameLabel(bet)}</span>
                    <StatusBadge status={bet.result || bet.status}>{resultLabel(bet.result)}</StatusBadge>
                  </div>
                  <div className="flex justify-between text-sm gap-2 mt-2">
                    <span className="text-gray-500">Number</span>
                    <span className="font-mono font-semibold text-hash-gold/95">{formatBetPickDisplay(bet)}</span>
                  </div>
                  <div className="flex justify-between text-sm gap-2">
                    <span className="text-gray-400">{new Date(bet.timestamp).toLocaleDateString()}</span>
                    <span className="text-gray-300">{bet.cost.toFixed(2)} USDT</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-gray-500">Prize</span>
                    <span className="text-hash-gold font-medium">{formatPrizeDisplay(bet)}</span>
                  </div>
                  <div className="mt-2 text-sm">
                    <TxLink txHash={bet.txHash} />
                  </div>
                </GlassCard>
              ))}
              {history.bets.length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  {historyRefreshing ? 'Data is loading...' : 'No bets found'}
                </div>
              )}
            </div>
          </div>

          <Pagination page={history.page} totalPages={history.totalPages} onPageChange={handlePageChange} />

          {/* AutoDraw Settings Section */}
          {/* <div className="mt-12">
            <h2 className="section-title text-2xl font-bold text-white text-center">
              <GradientText variant="hash" className="text-2xl font-bold">AutoDraw</GradientText>{' '}
              <GradientText variant="medora" className="text-2xl font-bold">Settings</GradientText>
            </h2>
            {autoDraw ? (
              <GlassCard variant="premium" className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <div className="text-gray-500 text-xs mb-1">Status</div>
                    <div className={`font-bold ${autoDraw.enabled ? 'text-emerald-400' : 'text-gray-500'}`}>
                      {autoDraw.enabled ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs mb-1">Game</div>
                    <div className="text-white font-bold">{GAMES[autoDraw.gameId]?.shortName || autoDraw.gameId}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs mb-1">Numbers per Entry</div>
                    <div className="text-white font-bold">{autoDraw.numberCount}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs mb-1">Frequency</div>
                    <div className="text-white font-bold capitalize">{autoDraw.frequency.replace('_', ' ')}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs mb-1">Max Entries</div>
                    <div className="text-white font-bold">{autoDraw.maxEntries}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs mb-1">Stop on Win</div>
                    <div className="text-white font-bold">{autoDraw.stopOnWin ? 'Yes' : 'No'}</div>
                  </div>
                </div>
                <button
                  onClick={() => setAutoDrawModalOpen(true)}
                  className="btn-blue-outline inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm"
                >
                  <img src="/images/icon-autodraw.svg" alt="" className="w-4 h-4" />
                  Configure AutoDraw
                </button>
              </GlassCard>
            ) : (
              <GlassCard variant="blue" className="p-6 text-center">
                <p className="text-gray-400 mb-4">No AutoDraw configuration found.</p>
                <button
                  onClick={() => setAutoDrawModalOpen(true)}
                  className="btn-blue-outline inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm"
                >
                  <img src="/images/icon-autodraw.svg" alt="" className="w-4 h-4" />
                  Configure AutoDraw
                </button>
              </GlassCard>
            )}
          </div> */}
        </div>
      </main>

      <AutoDrawModal isOpen={autoDrawModalOpen} onClose={() => setAutoDrawModalOpen(false)} />
      <FooterSection />
    </div>
  );
}

export default History;
